import { Autocomplete, TextField } from "@mui/material";
import * as numbers from "common/numbers";
import Disabled from "components/common/Disabled";
import * as labels from "config.labels";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";

const IncludeSir = ({ layer }) => {
  const dispatch = useDispatch();

  const sirs = useSelector(claimsSelectors.selectSelfInsuredRetentions) || [];

  const lossCategories = useSelector(
    (state) => state?.temp?.exposureMetrics?.lossCategories || []
  );
  const lossTypeMap = useMemo(() => {
    return Object.fromEntries(lossCategories.map((c) => [c.key, c.display]));
  }, [lossCategories]);

  const { data: retentionsConfig } = staticDataService.useConfigQuery(
    "retentions"
  );
  const typeMap = useMemo(() => {
    const entries = (retentionsConfig?.retentionTypes || []).map((t) => [
      t.key,
      t.display,
    ]);
    return Object.fromEntries(entries);
  }, [retentionsConfig?.retentionTypes]);
  const coverageMap = useMemo(() => {
    const entries = (
      retentionsConfig?.retentionCoverageCategory || []
    ).map((c) => [c.key, c.display]);
    return Object.fromEntries(entries);
  }, [retentionsConfig?.retentionCoverageCategory]);

  const includeSir = layer?.includeSir ?? null;
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const setIncludeSir = (id) => {
    dispatch(
      pricingActions.updateLayer({
        layerId: layer?.id,
        values: { includeSir: id },
      })
    );
  };

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={sirs.find((sir) => sir.id === includeSir) ?? null}
        onChange={(_, value) => setIncludeSir(value?.id ?? null)}
        options={sirs}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        getOptionLabel={(option) => {
          const valuePart = numbers.abbreviated(option?.value);
          const retentionTypeKey = option?.retentionType;
          const tokens = [];

          if (retentionTypeKey === "SIMPLE") {
            if (option?.lossType) {
              tokens.push(lossTypeMap[option.lossType] || option.lossType);
            }
          } else {
            if (retentionTypeKey) {
              tokens.push(typeMap[retentionTypeKey] || retentionTypeKey);
            }
            if (option?.retentionCoverageCategory) {
              tokens.push(
                coverageMap[option.retentionCoverageCategory] ||
                  option.retentionCoverageCategory
              );
            }
          }

          return tokens.length
            ? `${valuePart} - ${tokens.join(" - ")}`
            : `${valuePart}`;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={translateLabel(labels.ACTIVE_LAYER_PANEL_INCLUDE_SIR_LABEL, {
              default: "Include SIR",
            })}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
          />
        )}
        autoHighlight
      />
    </Disabled>
  );
};

export default IncludeSir;
